<app-nav></app-nav>

<main class="cookies-page">
  <!-- ─── Hero ─────────────────────────────────────────────────────── -->
  <section class="legal-hero brand-section">
    <div class="hero-orb hero-orb-blue"></div>
    <div class="hero-orb hero-orb-green"></div>

    <div class="legal-hero-inner">
      <span class="brand-eyebrow">Legal</span>
      <h1>Cookie Policy</h1>
      <p class="brand-lede">
        The short version: no tracking cookies, no ad pixels, no consent banner. Here is exactly what is stored and why.
      </p>
      <p class="last-updated">Last Updated: {{ lastUpdated }}</p>
    </div>
  </section>

  <!-- ─── Content ───────────────────────────────────────────────────── -->
  <section class="legal-body brand-section">
    <div class="legal-grid">

      <!-- Sticky nav -->
      <nav class="legal-toc" aria-label="Section navigation">
        <p class="toc-label">On this page</p>
        <ol class="toc-list">
          <li><a [routerLink]="['/cookies']" fragment="introduction">Introduction</a></li>
          <li><a [routerLink]="['/cookies']" fragment="what-are-cookies">Cookies &amp; similar tech</a></li>
          <li><a [routerLink]="['/cookies']" fragment="website">On our website</a></li>
          <li><a [routerLink]="['/cookies']" fragment="admin">Admin console</a></li>
          <li><a [routerLink]="['/cookies']" fragment="app-storage">In the mobile app</a></li>
          <li><a [routerLink]="['/cookies']" fragment="third-party">Third-party services</a></li>
          <li><a [routerLink]="['/cookies']" fragment="consent">Consent &amp; exemptions</a></li>
          <li><a [routerLink]="['/cookies']" fragment="your-choices">Your choices</a></li>
          <li><a [routerLink]="['/cookies']" fragment="dnt">Do Not Track</a></li>
          <li><a [routerLink]="['/cookies']" fragment="contact">Contact</a></li>
          <li><a [routerLink]="['/cookies']" fragment="changes">Policy changes</a></li>
        </ol>
      </nav>

      <!-- Body -->
      <div class="legal-content">

        <section id="introduction" class="legal-section">
          <h2>1. Introduction</h2>
          <p>
            This Cookie Policy explains how JCC Development LLC ("we," "our," or "us") uses cookies
            and similar technologies on the stml.app website and in the Skip To My Loo mobile
            application (together, the "Service"). It should be read together with our
            <a routerLink="/privacy">Privacy Policy</a>, which describes everything else we collect.
          </p>
          <p>
            <strong>We do not use cookies or similar technologies for advertising, cross-site
            tracking, or analytics on our website.</strong> The only storage we use is strictly
            necessary to make the Service work, and it is listed in full below.
          </p>
        </section>

        <section id="what-are-cookies" class="legal-section">
          <h2>2. Cookies and Similar Technologies</h2>
          <p>
            Cookies are small text files that a website places on your device so it can recognize
            your browser on later requests. "Similar technologies" include browser local storage
            and session storage, mobile app storage, software development kits (SDKs) embedded in
            an app, device identifiers, and tracking pixels. Privacy laws such as the EU ePrivacy
            Directive treat all of these alike, so this policy covers all of them, not just
            cookies in the narrow sense.
          </p>
        </section>

        <section id="website" class="legal-section">
          <h2>3. On Our Website (stml.app)</h2>
          <p>
            <strong>Our public website sets no first-party cookies</strong> and contains no
            analytics scripts, advertising tags, social media pixels, or embedded trackers.
          </p>
          <p>
            It does not write anything to your browser's local or session storage either. Pages
            such as this one, the download page, and the support page are served without any
            client-side storage at all.
          </p>
          <p>
            The one exception is the contact form, which is protected by <strong>Cloudflare Turnstile</strong>, an essential
            security and fraud-prevention technology. Turnstile may set a short-lived clearance
            cookie and collects minimal device and browser signals strictly to verify that a
            submission comes from a person rather than a bot. It does not track you across other
            websites. See Section 6.
          </p>
        </section>

        <section id="admin" class="legal-section">
          <h2>4. Admin Console (Staff Only)</h2>
          <p>
            A small part of the website is a password-protected console used only by our staff and
            moderators. Signing in there sets <strong>strictly necessary, first-party authentication
            cookies</strong> that identify the signed-in session. They are marked HttpOnly and
            Secure, are not used for tracking, and are deleted when the staff member signs out or
            the session expires. The console also uses local storage to remember the signed-in
            user's display name and a few interface preferences, such as whether the sidebar is
            collapsed. None of this applies to visitors who do not sign in to the console.
          </p>
        </section>

        <section id="app-storage" class="legal-section">
          <h2>5. In the Mobile App</h2>
          <p>
            Mobile apps do not use browser cookies. The Skip To My Loo app stores the following on
            your device, all of it strictly necessary to provide the features you use:
          </p>
          <ul>
            <li><strong>Authentication tokens:</strong> kept in your device's secure keychain or keystore so you stay signed in.</li>
            <li><strong>Preferences:</strong> theme, haptics, game settings, and similar choices.</li>
            <li><strong>Cached data:</strong> recently loaded restrooms and your favorites, so the App stays fast and can show saved places when you are offline.</li>
            <li><strong>Game progress:</strong> local high scores and statistics.</li>
            <li><strong>Pending submissions:</strong> a review or restroom you started but have not sent yet.</li>
          </ul>
          <p>
            You can clear all of this by signing out, by clearing the App's data in your device
            settings, or by uninstalling the App. Deleting your account removes the server-side
            copy as described in our <a routerLink="/privacy">Privacy Policy</a>.
          </p>
        </section>

        <section id="third-party" class="legal-section">
          <h2>6. Third-Party Services That Use Similar Technologies</h2>
          <p>
            A few services embedded in the Service use their own SDKs or identifiers. We have kept
            this list to the minimum needed to run the App, and none of them are used for
            cross-site or cross-app tracking of you by us.
          </p>

          <h3>Google AdMob (mobile app, free tier only)</h3>
          <p>
            The free tier of the App shows ads served by Google AdMob. We request
            <strong>non-personalized ads only</strong>, so AdMob does not use your advertising
            identifier to build an interest profile for our ads. AdMob may still read your device's
            advertising identifier and IP address to serve and cap ads and to detect fraud. STML
            Premium removes ads entirely. You can also reset or limit your advertising identifier
            in your device settings (iOS: Settings, Privacy &amp; Security, Tracking and Apple
            Advertising; Android: Settings, Privacy, Ads).
          </p>

          <h3>Sentry (mobile app)</h3>
          <p>
            Our crash-reporting SDK stores a random installation identifier on your device so that
            repeated crashes from the same installation can be grouped. It is not an advertising
            identifier and is not shared with anyone else.
          </p>

          <h3>Google Maps (mobile app)</h3>
          <p>
            The Maps SDK may cache map tiles on your device and may collect device and usage
            information under <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google's Privacy Policy</a>.
          </p>

          <h3>Cloudflare Turnstile (website contact form)</h3>
          <p>
            As described in Section 3, Turnstile is an essential anti-spam check. See
            <a href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noopener noreferrer">Cloudflare's Privacy Policy</a>.
          </p>

          <h3>Apple App Store, Google Play, and RevenueCat (mobile app)</h3>
          <p>
            Subscription purchases are handled by Apple or Google on your device. RevenueCat, our
            subscription platform, stores an anonymous app-user identifier on your device so your
            purchase can be matched to your account. No payment cookies are set by us, and we never
            receive your card details.
          </p>
        </section>

        <section id="consent" class="legal-section">
          <h2>7. Consent and the Strictly Necessary Exemption</h2>
          <p>
            Under the EU and UK ePrivacy rules, storage that is strictly necessary to provide a
            service you have asked for does not require consent. Everything in Sections 3, 4, and 5
            falls into that category: it exists only to keep you signed in, remember what you have
            chosen, submit forms, and make the App work offline. That is why our website has no
            cookie banner and why we will never ask you to "accept cookies."
          </p>
          <p>
            Advertising in the mobile app is different. Even non-personalized ads read a device
            identifier for frequency capping and fraud detection. Where applicable local law
            requires it (for example in the European Economic Area, the United Kingdom, and
            Switzerland), mobile advertising identifiers are accessed only after you give consent
            through an in-app prompt, and you can change that choice at any time in the App. Your
            choice of the free or Premium tier is explained in our
            <a routerLink="/privacy">Privacy Policy</a>.
          </p>
        </section>

        <section id="your-choices" class="legal-section">
          <h2>8. Your Choices</h2>
          <ul>
            <li>You can block or clear cookies and browser storage in your browser settings. On our website this only affects the contact form's spam check and, for staff, the admin console sign-in.</li>
            <li>You can clear the App's local data in your device settings or by uninstalling it.</li>
            <li>You can remove ads by subscribing to STML Premium, and you can reset or limit your advertising identifier in your device settings.</li>
            <li>You can disable location access in your device settings at any time.</li>
            <li>You can delete your account from Settings in the App.</li>
          </ul>
        </section>

        <section id="dnt" class="legal-section">
          <h2>9. Do Not Track and Global Privacy Control</h2>
          <p>
            Because our website does not track you across sites, there is nothing for a "Do Not
            Track" or Global Privacy Control signal to switch off. We treat these signals as an
            expression of your preference and will honor them if our practices ever change.
          </p>
        </section>

        <section id="contact" class="legal-section">
          <h2>10. Contact Information</h2>
          <p>If you have questions about this Cookie Policy or our data practices, please contact us at:</p>
          <div class="contact-block">
            <strong>JCC Development LLC</strong>
            <a href="mailto:privacy@stml.app">privacy&#64;stml.app</a>
            <a routerLink="/contact">Contact form</a>
            <!-- TODO: add the LLC's mailing address here once confirmed. -->
          </div>
        </section>

        <section id="changes" class="legal-section">
          <h2>11. Changes to This Policy</h2>
          <p>
            We may update this Cookie Policy from time to time, for example if we add or remove a
            third-party service. We will post the updated policy here and change the "Last Updated"
            date, and we will tell you in the App or by email before any change that would require
            your consent.
          </p>
        </section>

      </div>
    </div>
  </section>
</main>

<app-footer></app-footer>
